import React, { useMemo, useRef, useState } from 'react';
import { ModuleRegistry } from 'ag-grid-enterprise';
import type { ColDef, GetRowIdParams, RowClickedEvent } from 'ag-grid-enterprise';
import { ExternalFilterModule, type GridApi } from 'ag-grid-community';
import {
    FilterCriteria,
    InventoryAssetGroupDto,
    InventoryGroupBy,
    InventoryGroupRow,
    InventorySoftwareGroupDto,
    InventorySourceGroupDto,
    Page,
} from '@ampsec/platform-client';
import { getGridData } from 'Apis/library';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import { MdGridView } from '@react-icons/all-files/md/MdGridView';
import { MdLayers } from '@react-icons/all-files/md/MdLayers';
import { BiLaptop } from '@react-icons/all-files/bi/BiLaptop';
import { GridType } from 'Core-utils/types/types';
import ModeTab from 'Components/atoms/ModeTab/ModeTab';
import { AgGridBase, DefaultTextCellRenderer, useClientExternalTextSearch } from '../AgGridBase';
import { DEFAULT_BASE_COL_DEF } from '../AgGridBase/AgGridBase.config';
import InventoryDetailPanel, { InventorySelection } from './InventoryDetailPanel';
import InventoryStatRow from './InventoryStatRow';
import SourceCellRenderer from './cellRenderers/SourceCellRenderer';
import CountBarCellRenderer, { InventoryGridContext } from './cellRenderers/CountBarCellRenderer';
import VersionsCellRenderer from './cellRenderers/VersionsCellRenderer';
import CveCellRenderer from './cellRenderers/CveCellRenderer';
import './styles.scss';

ModuleRegistry.registerModules([ExternalFilterModule]);

const GROUP_TABS: { key: InventoryGroupBy; icon: React.ReactNode; title: string; subtitle: string }[] = [
    { key: 'software', icon: <MdGridView />, title: 'Software', subtitle: 'By application' },
    { key: 'source', icon: <MdLayers />, title: 'Source', subtitle: 'By package type' },
    { key: 'asset', icon: <BiLaptop />, title: 'Asset', subtitle: 'By device' },
];

// Source filter options for the software view. Values are `category` (the API's
// finest-grained source), stable per the agent taxonomy (APD-1755, Phase 4).
const SOURCE_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'All sources' },
    { value: 'macos_application', label: 'Applications' },
    { value: 'homebrew_formula', label: 'Homebrew formula' },
    { value: 'homebrew_cask', label: 'Homebrew cask' },
    { value: 'npm_package', label: 'npm' },
    { value: 'pnpm_global_package', label: 'pnpm global' },
    { value: 'vscode_extension', label: 'VS Code extension' },
    { value: 'cursor_extension', label: 'Cursor extension' },
    { value: 'pip_package', label: 'pip' },
    { value: 'go_module', label: 'Go module' },
    { value: 'cargo_crate', label: 'Cargo crate' },
    { value: 'managed_binary', label: 'Managed binary' },
    { value: 'mcp_server', label: 'Local MCP server' },
];

const lastSeenColumn = (): ColDef<InventoryGroupRow> => ({
    colId: 'lastSeenAt',
    headerName: 'Last seen',
    flex: 1,
    minWidth: 140,
    cellRenderer: DefaultTextCellRenderer,
    valueGetter: (p) => (p.data as { lastSeenAt?: string | null })?.lastSeenAt,
    cellRendererParams: { formatValue: (v: unknown) => (v ? DateTimeFormatter(String(v)) : '—') },
});

const softwareColumns: ColDef<InventoryGroupRow>[] = [
    {
        colId: 'name',
        headerName: 'Software',
        flex: 2,
        minWidth: 200,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventorySoftwareGroupDto)?.name,
    },
    {
        colId: 'sources',
        headerName: 'Source',
        flex: 1.6,
        minWidth: 190,
        sortable: false,
        cellRenderer: SourceCellRenderer,
        cellRendererParams: { maxPills: 2 },
        valueGetter: (p) => (p.data as InventorySoftwareGroupDto)?.sources ?? [],
    },
    {
        colId: 'versionCount',
        headerName: 'Versions',
        flex: 1,
        minWidth: 120,
        cellRenderer: VersionsCellRenderer,
        valueGetter: (p) => (p.data as InventorySoftwareGroupDto)?.versionCount,
    },
    {
        colId: 'deviceCount',
        headerName: 'Devices',
        flex: 1.4,
        minWidth: 150,
        cellRenderer: CountBarCellRenderer,
        valueGetter: (p) => (p.data as InventorySoftwareGroupDto)?.deviceCount,
    },
    {
        colId: 'publisher',
        headerName: 'Publisher',
        flex: 1.4,
        minWidth: 150,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventorySoftwareGroupDto)?.publisher,
    },
    {
        colId: 'cve',
        headerName: 'CVEs',
        flex: 0.9,
        minWidth: 110,
        sortable: false,
        cellRenderer: CveCellRenderer,
    },
    lastSeenColumn(),
];

const sourceColumns: ColDef<InventoryGroupRow>[] = [
    {
        colId: 'source',
        headerName: 'Source',
        flex: 2,
        minWidth: 200,
        sortable: false,
        cellRenderer: SourceCellRenderer,
        valueGetter: (p) => (p.data as InventorySourceGroupDto)?.source,
    },
    {
        colId: 'ecosystems',
        headerName: 'Ecosystems',
        flex: 1.5,
        minWidth: 160,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventorySourceGroupDto)?.ecosystems?.join(', '),
    },
    {
        colId: 'titleCount',
        headerName: 'Titles',
        flex: 1.4,
        minWidth: 150,
        cellRenderer: CountBarCellRenderer,
        valueGetter: (p) => (p.data as InventorySourceGroupDto)?.titleCount,
    },
    {
        colId: 'deviceCount',
        headerName: 'Devices',
        flex: 1,
        minWidth: 110,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventorySourceGroupDto)?.deviceCount,
    },
    lastSeenColumn(),
];

const assetColumns: ColDef<InventoryGroupRow>[] = [
    {
        colId: 'sn',
        headerName: 'Device',
        flex: 2,
        minWidth: 200,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventoryAssetGroupDto)?.sn,
    },
    {
        colId: 'uid',
        headerName: 'Owner',
        flex: 1.5,
        minWidth: 160,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventoryAssetGroupDto)?.uid,
    },
    {
        colId: 'titleCount',
        headerName: 'Software items',
        flex: 1.5,
        minWidth: 160,
        cellRenderer: CountBarCellRenderer,
        valueGetter: (p) => (p.data as InventoryAssetGroupDto)?.titleCount,
    },
    {
        colId: 'installCount',
        headerName: 'Installs',
        flex: 1,
        minWidth: 110,
        cellRenderer: DefaultTextCellRenderer,
        valueGetter: (p) => (p.data as InventoryAssetGroupDto)?.installCount,
    },
    lastSeenColumn(),
];

const COLUMNS_BY_GROUP: Record<InventoryGroupBy, ColDef<InventoryGroupRow>[]> = {
    software: softwareColumns,
    source: sourceColumns,
    asset: assetColumns,
};

// Which numeric field drives the mini-bar (and its shared max) per grouping.
const BAR_FIELD_BY_GROUP: Record<InventoryGroupBy, 'deviceCount' | 'titleCount'> = {
    software: 'deviceCount',
    source: 'titleCount',
    asset: 'titleCount',
};

const rowKey = (row: InventoryGroupRow | undefined, groupBy: InventoryGroupBy): string => {
    if (!row) {
        return '';
    }
    if (groupBy === 'software') {
        return `sw:${(row as InventorySoftwareGroupDto).name}`;
    }
    if (groupBy === 'source') {
        return `src:${(row as InventorySourceGroupDto).source}`;
    }
    return `dev:${(row as InventoryAssetGroupDto).sn}`;
};

const SEARCH_FIELDS = [
    (row: InventoryGroupRow) => (row as InventorySoftwareGroupDto).name,
    (row: InventoryGroupRow) => (row as InventorySourceGroupDto).source,
    (row: InventoryGroupRow) => (row as InventoryAssetGroupDto).sn,
    (row: InventoryGroupRow) => (row as InventorySoftwareGroupDto).publisher,
    (row: InventoryGroupRow) => ((row as InventorySoftwareGroupDto).sources ?? []).join(' '),
    (row: InventoryGroupRow) => (row as InventoryAssetGroupDto).uid,
];

/**
 * Inventory dashboard grid (APD-1755). Pivots device_software_inventory three ways
 * via the SDK grouped read; row-click drills to versions/devices (software), that
 * device's software (asset), or re-filters software by the clicked source.
 *
 * Source values render as friendly pills, the headline count as a proportional
 * mini-bar, and a reserved CVE column stays wired off until the matcher (APD-1650)
 * lands. Client-side search and a source (category) filter sit above the grid.
 * OS-default filtering is deferred — there is no backend signal for it yet.
 */
const InventoryGrid: React.FC = () => {
    const [groupBy, setGroupBy] = useState<InventoryGroupBy>('software');
    const [sourceFilter, setSourceFilter] = useState<string | null>(null);
    const [selection, setSelection] = useState<InventorySelection | null>(null);
    const gridApiRef = useRef<GridApi<InventoryGroupRow> | null>(null);

    const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
        useClientExternalTextSearch<InventoryGroupRow>({
            fields: SEARCH_FIELDS,
            onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
        });

    const filter: FilterCriteria = useMemo(() => {
        const next: FilterCriteria = { groupBy, limit: 250 };
        if (groupBy === 'software' && sourceFilter) {
            next.category = sourceFilter;
        }
        return next;
    }, [groupBy, sourceFilter]);

    const { data } = useApiObjectData<Page<InventoryGroupRow>, Page<InventoryGroupRow>>(
        'deviceSoftwareInventory',
        (cacheKey: GridType, currentFilter?: FilterCriteria) => getGridData(cacheKey, currentFilter, 'grouped'),
        (response) => response,
        filter,
    );

    const rows = useMemo(() => data?.data ?? [], [data]);

    const gridContext: InventoryGridContext = useMemo(() => {
        const field = BAR_FIELD_BY_GROUP[groupBy];
        const maxBarValue = rows.reduce((max, row) => {
            const value = (row as Record<string, unknown>)[field];
            return typeof value === 'number' && value > max ? value : max;
        }, 0);
        return { maxBarValue };
    }, [rows, groupBy]);

    const stableDefaultColDef = useMemo(
        () => ({ ...DEFAULT_BASE_COL_DEF, resizable: false }) as ColDef<InventoryGroupRow>,
        [],
    );

    const selectGroup = (next: InventoryGroupBy) => {
        setSourceFilter(null);
        setGroupBy(next);
    };

    const handleRowClicked = (event: RowClickedEvent<InventoryGroupRow>) => {
        const row = event.data;
        if (!row) {
            return;
        }
        if (groupBy === 'software') {
            setSelection({ mode: 'software', name: (row as InventorySoftwareGroupDto).name });
        } else if (groupBy === 'source') {
            // Drill a source into its software list rather than a detail panel.
            setSourceFilter((row as InventorySourceGroupDto).source);
            setGroupBy('software');
        } else {
            setSelection({
                mode: 'asset',
                sn: (row as InventoryAssetGroupDto).sn,
                uid: (row as InventoryAssetGroupDto).uid,
            });
        }
    };

    if (selection) {
        return <InventoryDetailPanel selection={selection} onBack={() => setSelection(null)} />;
    }

    return (
        <div className="inventory-grid d-flex flex-column gap-3 h-100">
            <InventoryStatRow />
            <div className="inventory-grid__toolbar">
                {GROUP_TABS.map((tab) => (
                    <ModeTab
                        key={tab.key}
                        active={groupBy === tab.key}
                        icon={tab.icon}
                        title={tab.title}
                        subtitle={tab.subtitle}
                        onClick={() => selectGroup(tab.key)}
                    />
                ))}
                {groupBy === 'software' && (
                    <select
                        className="form-select form-select-sm ms-2 inventory-grid__source-select"
                        value={sourceFilter ?? ''}
                        onChange={(e) => setSourceFilter(e.target.value || null)}
                        aria-label="Filter by source"
                    >
                        {SOURCE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                )}
            </div>
            <AgGridBase.Root>
                <AgGridBase.Header hidden />
                <AgGridBase.Table<InventoryGroupRow>
                    columnDefs={COLUMNS_BY_GROUP[groupBy]}
                    dataMode="client"
                    rowData={rows}
                    showSidebar={false}
                    bodyHeight="100%"
                    defaultColDef={stableDefaultColDef}
                    showSearchBar={true}
                    onSearchChange={handleSearchChange}
                    agGridProps={{
                        context: gridContext,
                        suppressCellFocus: true,
                        getRowId: (params: GetRowIdParams<InventoryGroupRow>) => rowKey(params.data, groupBy),
                        onRowClicked: handleRowClicked,
                        isExternalFilterPresent,
                        doesExternalFilterPass,
                        onGridReady: (e) => {
                            gridApiRef.current = e.api;
                        },
                        noRowsOverlayComponentParams: {
                            noDescription: false,
                            description: 'No inventory found',
                        },
                    }}
                />
            </AgGridBase.Root>
        </div>
    );
};

export default InventoryGrid;
